import React, { useState, useEffect } from "react";
import { Box, Paper, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomLabel from "../components/CustomLabel";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import CustomTextField from "../components/CustomTextField";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";
import {
  updateVenue,
  getAllVenues,
  deleteVenue,
} from "../services/VenueService";
import {
  updateBranch,
  getAllBranches,
  deleteBranch,
  getAllBusinessTypes,
} from "../services/BranchesService";
import { toast } from "react-toastify";

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

const venueFields = [
  { type: "text" as const, name: "name", label: "Venue Name" },
  { type: "text" as const, name: "venueOverlay", label: "Venue Overlay" },
  {
    type: "number" as const,
    name: "orderNumber",
    label: "Order Number",
  },
  { type: "file" as const, name: "banner", label: "Venue Banner" },
];

let branchFields: Field[] = [];
const deleteFields: Field[] = [
  {
    name: "name",
    label: "Name",
    required: true,
    customRender: (value: any) => (
      <CustomTextField label="Name" value={value || ""} disabled fullWidth />
    ),
  },
  { type: "password", name: "password", label: "Password", required: true },
];

const EditVenues: React.FC = () => {
  const [venues, setVenues] = useState<any[]>([]);
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [selectedVenueIdx, setSelectedVenueIdx] = useState<number | null>(null);
  const [selectedBranchIdx, setSelectedBranchIdx] = useState<number | null>(
    null
  );
  const [venueTypes, setVenueTypes] = useState<{ id: string; name: string }[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { type: "venue"; vIdx: number }
    | { type: "branch"; vIdx: number; bIdx: number }
    | null
  >(null);
  const [businessTypes, setBusinessTypes] = useState<
    { id: string; name: string }[]
  >([]);

  const loadVenuesWithBranches = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllVenues();
      if (response && Array.isArray(response)) {
        const types = response.map((venue: any) => ({
          id: venue.id || venue._id || venue.venue_id || String(venue.name),
          name: venue.name || "Unknown",
        }));
        setVenueTypes(types);

        const venuesWithBranches = await Promise.all(
          response.map(async (v: any) => {
            const venueId = v.id || v._id || v.venue_id;
            let branches: any[] = [];
            try {
              if (venueId) {
                const res = await getAllBranches(String(venueId));
              const businesses = Array.isArray(res)
                ? res
                : Array.isArray(res?.businesses)
                ? res.businesses
                : Array.isArray(res?.data?.businesses)
                ? res.data.businesses
                : Array.isArray(res?.data)
                ? res.data
                : Array.isArray(res?.venue?.businesses)
                ? res.venue.businesses
                : [];
              branches = (businesses || []).map((b: any) => ({
                id: b.id || b._id || b.business_id,
                name: b.name,
                sender: b.sender,
                image: b.image || b.logo || "",
                description: b.description || b.business_description || "",
                address: b.address,
                priorCancellationHours:
                  b.prior_cancel_hours || b.priorCancellationHours || "",
                prior_cancel_hours: b.prior_cancel_hours,
                phone: b.business_phone || b.phone,
                business_phone: b.business_phone,
                short_name: b.short_name,
                venue_id: b.venue_id,
                latitude: b.latitude,
                longitude: b.longitude,
                type_id: b.type_id || "",
                type_name: b.type_name || "",
                orderNumber: (() => {
                  const ov = b.order ?? b.orderNumber ?? b.order_number ?? "";
                  if (typeof ov === "number") return ov;
                  if (ov !== "" && !Number.isNaN(Number(ov))) return Number(ov);
                  return "";
                })(),
              }));
            }
          } catch (e) {
            branches = [];
          }

            const bannerUrl =
              v.banner || v.logo || v.image || v.image_url || "/logo192.png";
            const orderValue = v.order ?? v.orderNumber ?? v.order_number ?? "";
            const overlayValue =
              v.venueOverlay ?? v.venue_overlay ?? v.overlay ?? "";

            return {
              id: venueId,
              orderNumber:
                typeof orderValue === "number"
                  ? orderValue
                  : orderValue !== "" && !Number.isNaN(Number(orderValue))
                  ? Number(orderValue)
                  : "",
              name: v.name || "Unknown Venue",
              image: bannerUrl,
              banner: v.banner || "",
              venueOverlay: overlayValue,
              branches,
            };
          })
        );
        setVenues(venuesWithBranches);
      }
    } catch (e) {
      setVenueTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenuesWithBranches();
  }, [loadVenuesWithBranches]);

  const fetchBusinessTypes = async () => {
    try {
      const response = await getAllBusinessTypes();
      console.log("Business types response:", response);
      if (Array.isArray(response)) {
        const normalized = response
          .map((type: any) => {
            const id =
              type?.id ??
              type?.type_id ??
              type?._id ??
              type?.value ??
              type?.uuid;
            const name =
              type?.name ??
              type?.value ??
              type?.type_name ??
              type?.label ??
              type?.title ??
              type?.description;

            if (!id || !name) return null;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];

        const unique = Array.from(
          new Map(normalized.map((type) => [type.id, type])).values()
        ).sort((a, b) => a.name.localeCompare(b.name));

        setBusinessTypes(unique);
      } else {
        setBusinessTypes([]);
      }
    } catch (error) {
      console.error("Failed to fetch business types:", error);
      setBusinessTypes([]);
    }
  };

  React.useEffect(() => {
    fetchBusinessTypes();
  }, []);

  branchFields = [
    { type: "text", name: "name", label: "Branch Name", required: true },
    {
      type: "number",
      name: "orderNumber",
      label: "Order Number",
    },
    { type: "text", name: "short_name", label: "Email Sender", required: true },
    {
      name: "type_id",
      label: "Business Type",
      type: "dropdown",
      options: businessTypes.map((type) => ({
        label: type.name,
        value: type.id,
      })),
      required: true,
    },
    { type: "text", name: "description", label: "Description" },

    {
      name: "address",
      label: "Address",
      colSpan: 2,
      customRender: (
        value: any,
        onChange: (v: any) => void,
        helpers?: {
          setFieldValue: (name: string, value: any) => void;
          values?: Record<string, any>;
        },
        error?: string
      ) => {
        return (
          <AddressAutocomplete
            value={value}
            onChange={onChange}
            helpers={helpers}
            error={error}
          />
        );
      },
    },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => `+${c.phone}`),
      required: true,
    },
    {
      type: "text",
      name: "phoneNumber",
      label: "Phone Number",
      required: true,
    },

    {
      type: "text",
      name: "prior_cancel_hours",
      label: "Prior Cancellation Hours",
      required: true,
    },

    { type: "file", name: "image", label: "Branch Image" },
  ];

  const handleEditVenue = (idx: number) => {
    setSelectedVenueIdx(idx);
    setVenueDialogOpen(true);
  };

  const openDeleteVenueDialog = (vIdx: number) => {
    setDeleteTarget({ type: "venue", vIdx });
    setDeleteDialogOpen(true);
  };
  const handleVenueDialogSubmit = async (values: Record<string, any>) => {
    if (selectedVenueIdx !== null) {
      const current = venues[selectedVenueIdx] as any;
      const venueId = current?.id || current?._id || current?.venue_id;
      const updatedVenues = [...venues];

      const currentBanner = current.banner || current.image;
      const rawBannerInput =
        values.banner !== undefined ? values.banner : values.image;

      if (
        rawBannerInput instanceof File &&
        rawBannerInput.size > MAX_IMAGE_SIZE_BYTES
      ) {
        toast.error("Image must be 2MB or smaller.");
        return;
      }

      const parsedOrderNumber =
        values.orderNumber !== undefined && values.orderNumber !== ""
          ? parseInt(String(values.orderNumber), 10)
          : undefined;

      let bannerValue = currentBanner;
      if (
        rawBannerInput &&
        typeof rawBannerInput === "object" &&
        rawBannerInput instanceof File
      ) {
        bannerValue = URL.createObjectURL(rawBannerInput);
      } else if (
        rawBannerInput &&
        typeof rawBannerInput === "string" &&
        rawBannerInput.trim() !== ""
      ) {
        bannerValue = rawBannerInput;
      }

      updatedVenues[selectedVenueIdx] = {
        ...updatedVenues[selectedVenueIdx],
        name: values.name,
        venueOverlay:
          values.venueOverlay ??
          updatedVenues[selectedVenueIdx].venueOverlay ??
          "",
        image: bannerValue,
        banner: bannerValue, // This will be used as banner
        orderNumber:
          parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)
            ? parsedOrderNumber
            : updatedVenues[selectedVenueIdx].orderNumber,
      } as any;
      setVenues(updatedVenues);

      if (venueId) {
        try {
          const payload: Record<string, any> = { name: values.name };
          if (
            parsedOrderNumber !== undefined &&
            !Number.isNaN(parsedOrderNumber)
          ) {
            payload.orderNumber = parsedOrderNumber;
            payload.order = parsedOrderNumber;
          }
          if (values.venueOverlay !== undefined) {
            payload.venueOverlay = values.venueOverlay ?? "";
          }

          if (
            rawBannerInput &&
            typeof rawBannerInput === "object" &&
            rawBannerInput instanceof File
          ) {
            payload.banner = rawBannerInput;
          } else if (
            typeof bannerValue === "string" &&
            bannerValue.trim() !== "" &&
            !bannerValue.startsWith("blob:")
          ) {
            payload.banner = bannerValue;
          } else if (
            typeof currentBanner === "string" &&
            currentBanner.trim() !== "" &&
            !currentBanner.startsWith("blob:")
          ) {
            payload.banner = currentBanner;
          }

          await updateVenue(String(venueId), payload);
          toast.success("Venue updated successfully");
          await loadVenuesWithBranches();
        } catch (e: any) {
          const msg =
            e?.response?.data?.error || e?.message || "Failed to update venue";
          toast.error(msg);
        }
      }
    }
    setVenueDialogOpen(false);
    setSelectedVenueIdx(null);
  };

  const handleEditBranch = (venueIdx: number, branchIdx: number) => {
    setSelectedVenueIdx(venueIdx);
    setSelectedBranchIdx(branchIdx);
    setBranchDialogOpen(true);
  };

  const openDeleteBranchDialog = (venueIdx: number, branchIdx: number) => {
    setDeleteTarget({ type: "branch", vIdx: venueIdx, bIdx: branchIdx });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogSubmit = async (values: Record<string, any>) => {
    try {
      if (!deleteTarget) return;
      if (deleteTarget.type === "venue") {
        const v = venues[deleteTarget.vIdx] as any;
        const venueId = v?.id || v?._id || v?.venue_id;
        if (venueId)
          await deleteVenue(String(venueId), { password: values.password });
        setVenues((prev) => prev.filter((_, i) => i !== deleteTarget.vIdx));
        toast.success("Venue deleted successfully");
      } else {
        const v = venues[deleteTarget.vIdx] as any;
        const b = v?.branches?.[deleteTarget.bIdx] as any;
        const branchId = b?.id || b?._id || b?.business_id;
        if (branchId)
          await deleteBranch(String(branchId), { password: values.password });
        setVenues((prev) =>
          prev.map((ven, i) => {
            if (i !== deleteTarget.vIdx) return ven;
            return {
              ...ven,
              branches: ven.branches.filter(
                (_: any, j: number) => j !== deleteTarget.bIdx
              ),
            };
          })
        );
        toast.success("Branch deleted successfully");
      }
    } catch (e) {
      const msg =
        (e as any)?.response?.data?.error ||
        (e as any)?.message ||
        "Delete failed";
      toast.error(msg);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };
  const handleBranchDialogSubmit = async (values: Record<string, any>) => {
    if (selectedVenueIdx !== null && selectedBranchIdx !== null) {
      const v = venues[selectedVenueIdx] as any;
      const b = (v?.branches?.[selectedBranchIdx] as any) || {};
      const branchId = b?.id || b?._id || b?.business_id;
      const branchImageInput = values.image;

      if (
        branchImageInput instanceof File &&
        branchImageInput.size > MAX_IMAGE_SIZE_BYTES
      ) {
        toast.error("Image must be 2MB or smaller.");
        return;
      }

      const fieldMapping: Record<string, string> = {
        name: "name",
        orderNumber: "order",
        short_name: "short_name",
        address: "address",
        fn_number: "fn_number",
        phoneNumber: "business_phone",
        countryCode: "countryCode",
        prior_cancel_hours: "prior_cancel_hours",
        latitude: "latitude",
        longitude: "longitude",
        image: "image",
        venue_id: "venue_id",
        description: "business_description",
        type_id: "type_id",
      };

      let businessPhone = "";
      if (values.countryCode && values.phoneNumber) {
        const cc = String(values.countryCode).replace(/\s/g, "");
        businessPhone = `${cc}${values.phoneNumber}`;
      } else if (values.phoneNumber) {
        businessPhone = String(values.phoneNumber);
      }

      const parsedBranchOrder =
        values.orderNumber !== undefined && values.orderNumber !== ""
          ? parseInt(String(values.orderNumber), 10)
          : undefined;

      const payload: any = {};

      // venue_id is now directly the ID (not the name)
      if (values.venue_id) {
        payload.venue_id = values.venue_id;
      }

      // Handle type_id and type_name for business type
      if (values.type_id) {
        const selectedType = businessTypes.find(
          (type) => String(type.id) === String(values.type_id)
        );
        const typeIdToSend = selectedType?.id ?? String(values.type_id);
        const typeNameToSend = selectedType?.name ?? String(values.type_id);

        payload.type_id = typeIdToSend;
        payload.type_name = typeNameToSend;
      }

      Object.keys(values).forEach((key) => {
        const backendKey = fieldMapping[key] || key;
        // Skip fields handled separately or not sent directly
        if (
          [
            "phoneNumber",
            "countryCode",
            "type_id",
            "type_name",
            "venue_id",
          ].includes(key)
        )
          return;
        const val = values[key];
        // Allow clearing description by sending an empty string
        if (key === "description") {
          payload[backendKey] = val ?? "";
          return;
        }
        // Allow clearing address by sending an empty string
        if (key === "address") {
          payload[backendKey] = val ?? "";
          return;
        }
        // Allow clearing coordinates by sending null
        if (key === "latitude" || key === "longitude") {
          if (val === undefined || val === null || String(val).trim() === "") {
            payload[backendKey] = null;
            return;
          }
          payload[backendKey] = val;
          return;
        }
        if (val !== undefined && val !== null && val !== "")
          payload[backendKey] = val;
      });

      const isAddressEmpty = !String(values.address ?? "").trim();
      if (isAddressEmpty) {
        payload.address = "";
        payload.latitude = null;
        payload.longitude = null;
      }

      // Include a plain description key for backward compatibility if needed
      if (values.description !== undefined && values.description !== null) {
        payload.description = values.description;
      }

      if (businessPhone) payload.business_phone = businessPhone;
      if (
        parsedBranchOrder !== undefined &&
        !Number.isNaN(parsedBranchOrder)
      ) {
        payload.orderNumber = parsedBranchOrder;
        payload.order = parsedBranchOrder;
      }

      const updatedVenues = [...venues] as any[];
      updatedVenues[selectedVenueIdx].branches[selectedBranchIdx] = {
        ...b,
        name: values.name,
        description: values.description,
        address: values.address,
        priorCancellationHours: values.prior_cancel_hours,
        phone: businessPhone || b.phone,
        short_name: values.short_name,
        type_id: values.type_id,
        type_name: (() => {
          const selectedType = businessTypes.find(
            (type) => String(type.id) === String(values.type_id)
          );
          return selectedType?.name || "";
        })(),
        latitude: isAddressEmpty ? null : values.latitude,
        longitude: isAddressEmpty ? null : values.longitude,
        orderNumber:
          parsedBranchOrder !== undefined && !Number.isNaN(parsedBranchOrder)
            ? parsedBranchOrder
            : b.orderNumber,
      };
      setVenues(updatedVenues as any);

      if (branchId) {
        try {
          const hasFile =
            values.image &&
            typeof values.image === "object" &&
            values.image instanceof File;
          if (hasFile) {
            const formData = new FormData();
            Object.entries(payload).forEach(([k, v]) => {
              if (v !== undefined && v !== null) {
                formData.append(k, String(v));
              }
            });
            formData.append("image", values.image as File);
            await updateBranch(String(branchId), formData as any);
          } else {
            await updateBranch(String(branchId), payload as any);
          }
          toast.success("Branch updated successfully");
          try {
            // Refresh only that branch from server to update server-derived fields
          const branchesResp = await getAllBranches(String(v.id || v._id || v.venue_id));
          const businesses = Array.isArray(branchesResp)
            ? branchesResp
            : Array.isArray(branchesResp?.businesses)
            ? branchesResp.businesses
              : Array.isArray(branchesResp?.data?.businesses)
              ? branchesResp.data.businesses
              : Array.isArray(branchesResp?.data)
              ? branchesResp.data
              : Array.isArray(branchesResp?.venue?.businesses)
              ? branchesResp.venue.businesses
              : [];

            const branchList = (businesses || []).map((b: any) => ({
              id: b.id || b._id || b.business_id,
              name: b.name,
              sender: b.sender,
              image: b.image || b.logo || "",
              description: b.description || b.business_description || "",
              address: b.address,
              priorCancellationHours:
                b.prior_cancel_hours || b.priorCancellationHours || "",
              prior_cancel_hours: b.prior_cancel_hours,
              phone: b.business_phone || b.phone,
              business_phone: b.business_phone,
              short_name: b.short_name,
              venue_id: b.venue_id,
              latitude: b.latitude,
              longitude: b.longitude,
              type_id: b.type_id || "",
              type_name: b.type_name || "",
              orderNumber: (() => {
                const ov = b.order ?? b.orderNumber ?? b.order_number ?? "";
                if (typeof ov === "number") return ov;
                if (ov !== "" && !Number.isNaN(Number(ov))) return Number(ov);
                return "";
              })(),
            }));

            setVenues((prev) =>
              prev.map((pv, i) =>
                i === selectedVenueIdx ? { ...pv, branches: branchList } : pv
              )
            );
          } catch (e) {
            // best effort only — ignore
          }
        } catch (e: any) {
          const msg =
            e?.response?.data?.error || e?.message || "Failed to update branch";
          toast.error(msg);
        }
      }
    }
    setBranchDialogOpen(false);
    setSelectedBranchIdx(null);
    setSelectedVenueIdx(null);
  };

  const selectedVenue =
    selectedVenueIdx !== null ? venues[selectedVenueIdx] : null;
  const selectedBranch =
    selectedVenueIdx !== null && selectedBranchIdx !== null
      ? venues[selectedVenueIdx]?.branches?.[selectedBranchIdx]
      : null;

  return (
    <Box sx={{ p: 4 }}>
      <CustomLabel
        text="Edit Venues"
        fontWeight={900}
        fontSize="2rem"
        color={COLORS.MainColor}
        sx={{ mb: 4, textAlign: "center" }}
      />
      {loading && (!venues || venues.length === 0) ? (
        <Box sx={{ textAlign: "center", color: COLORS.BlackText, mb: 2 }}>
          Loading venues and branches...
        </Box>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gap: 4,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
        }}
      >
        {venues.map((venue, vIdx) => (
          <Paper
            key={venue.name + vIdx}
            sx={{ borderRadius: 4, boxShadow: 3, overflow: "hidden" }}
          >
            <Box
              sx={{
                width: "100%",
                height: 180,
                background: `url(${venue.image}) center/cover no-repeat`,
                borderRadius: "4px 4px 0 0",
              }}
            />
            <Box sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <CustomLabel
                  text={venue.name}
                  fontWeight={700}
                  fontSize="1.3rem"
                  color={COLORS.MainColor}
                />
                <IconButton
                  onClick={() => handleEditVenue(vIdx)}
                  sx={{ ml: "auto" }}
                >
                  <EditIcon color="primary" />
                </IconButton>
                <IconButton onClick={() => openDeleteVenueDialog(vIdx)}>
                  <DeleteIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Box>
              <CustomLabel
                text="Branches"
                fontWeight={700}
                fontSize="1.1rem"
                color={COLORS.MainColor}
                sx={{ mt: 2, mb: 1 }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {venue.branches.map((branch: any, bIdx: number) => (
                  <Paper
                    key={branch.name + bIdx}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      boxShadow: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: `url(${branch.image}) center/cover no-repeat`,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <CustomLabel
                        text={
                          branch.name +
                          (branch.sender ? ` (${branch.sender})` : "")
                        }
                        fontWeight={600}
                        fontSize="1rem"
                        color={COLORS.MainColor}
                      />
                      {branch.description && (
                        <CustomLabel
                          text={branch.description}
                          fontWeight={400}
                          fontSize="0.95rem"
                          color={COLORS.MainColor}
                        />
                      )}
                    </Box>
                    <IconButton onClick={() => handleEditBranch(vIdx, bIdx)}>
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton
                      onClick={() => openDeleteBranchDialog(vIdx, bIdx)}
                    >
                      <DeleteIcon sx={{ color: "#d32f2f" }} />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
      <GenericFormDialog
        open={venueDialogOpen}
        title="Edit Venue"
        onClose={() => {
          setVenueDialogOpen(false);
          setSelectedVenueIdx(null);
        }}
        onSubmit={handleVenueDialogSubmit}
        fields={venueFields}
        initialValues={
          selectedVenue
            ? {
                name: selectedVenue?.name || "",
                orderNumber:
                  selectedVenue?.orderNumber !== undefined &&
                  selectedVenue?.orderNumber !== null
                    ? selectedVenue?.orderNumber
                    : "",
                venueOverlay:
                  selectedVenue?.venueOverlay ??
                  (selectedVenue as any)?.venue_overlay ??
                  "",
                banner:
                  selectedVenue?.banner || selectedVenue?.image,
              }
            : {}
        }
      />
      <GenericFormDialog
        open={branchDialogOpen}
        title="Edit Branch"
        onClose={() => {
          setBranchDialogOpen(false);
          setSelectedBranchIdx(null);
          setSelectedVenueIdx(null);
        }}
        onSubmit={handleBranchDialogSubmit}
        fields={branchFields}
        initialValues={
          selectedBranch
            ? {
                name: (selectedBranch as any)?.name || "",
                short_name:
                  (selectedBranch as any)?.short_name || "",
                type_id:
                  (selectedBranch as any)?.type_id || "",
                venue_id: (() => {
                  const br = (selectedBranch as any) || {};
                  return String(br?.venue_id || "");
                })(),
                description:
                  (selectedBranch as any)?.description ||
                  (selectedBranch as any)?.business_description ||
                  "",
                address:
                  (selectedBranch as any)?.address || "",
                prior_cancel_hours:
                  (selectedBranch as any)?.prior_cancel_hours ||
                  (selectedBranch as any)?.priorCancellationHours ||
                  "",
                countryCode: (() => {
                  const p =
                    (selectedBranch as any)?.business_phone ||
                    (selectedBranch as any)?.phone ||
                    "";
                  const match = String(p).match(/^(\+\d{1,3})/);
                  return match ? match[1] : "+961";
                })(),
                phoneNumber: (() => {
                  const p =
                    (selectedBranch as any)?.business_phone ||
                    (selectedBranch as any)?.phone ||
                    "";
                  return String(p).replace(/^(\+\d{1,3})/, "");
                })(),
                latitude:
                  (selectedBranch as any)?.latitude || "",
                longitude:
                  (selectedBranch as any)?.longitude || "",
                orderNumber:
                  (selectedBranch as any)?.orderNumber ?? "",
              }
            : {}
        }
      />
      {/* Delete confirmation dialog */}
      <GenericFormDialog
        open={deleteDialogOpen}
        title={
          deleteTarget?.type === "venue" ? "Delete Venue" : "Delete Branch"
        }
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onSubmit={handleDeleteDialogSubmit}
        fields={deleteFields}
        submitLabel="Delete"
        initialValues={((): Record<string, string> => {
          if (!deleteTarget) return { name: "", password: "" };
          if (deleteTarget.type === "venue") {
            const v = venues[deleteTarget.vIdx];
            return { name: v?.name || "", password: "" };
          }
          const b = venues[deleteTarget.vIdx]?.branches?.[deleteTarget.bIdx];
          return { name: b?.name || "", password: "" };
        })()}
      />
    </Box>
  );
};

export default EditVenues;
